import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import ToggleDropdown from '../reused-components/SidebarTabs';
import AddProducts from '../reused-components/AddProducts';
import Profile from '../reused-components/Profile';
import DarkModeSwitch from '../reused-components/ToggleDarkMode';
import DownloadDropdown from '../reused-components/DownloadDropdown';
import FeatureUsageFilters from '../reused-components/FeatureUsageFilters';
import FeatureUsageBarChart, { FEATURE_COLORS } from '../reused-components/Charts/FeatureUsageBarChart';
import FeatureUsageTable from '../tables/FeatureUsageTable';
import { fetchFeatureUsage } from '../../services/apiService';
import { useTelemetryContext } from '../../contexts/TelemetryContext';
import { localeMapping } from '../../utils/constants';
import {
    CHART_LIMIT,
    DEFAULT_FEATURE_FILTERS,
    FEATURE_CSV_COLUMNS,
    activeFilterChips,
    buildFeatureUsageParams,
    featureUsageQueryString,
    featureCsvRows,
    filterAndSortFeatures,
    formatIsoDate,
    formatNumber,
    isCustomRangeIncomplete,
    parseFeatureUsageQuery,
    readSavedFeatureView,
    saveFeatureView,
} from '../../utils/featureUsage';
import smlogo from '../../img/sm-logo.svg';
import profileImage from '../../img/user-profile.png';
import './TopFeatures.css';

const localeLabel = (code) => localeMapping[code] || code;

export default function TopFeatures() {
    document.title = 'Top Features | Telemetry';

    const { storeId, productId } = useParams();
    const { selectedProduct } = useTelemetryContext();

    // The URL holds the view (filters, search, order): refresh, Back/Forward and shared links
    // all show the same thing. Arriving without a query (e.g. from the sidebar) restores this
    // product's last view in this tab.
    const [searchParams, setSearchParams] = useSearchParams();
    const urlQuery = searchParams.toString();
    // Only on arrival (first render for this product): a URL that becomes empty later means the
    // user went back to the default view (Clear all, Back), which must not be overridden.
    const arrivedFor = useRef(null);
    const isArrival = arrivedFor.current !== productId;
    const savedQuery = !urlQuery && isArrival ? readSavedFeatureView(productId) : '';
    const effectiveQuery = urlQuery || savedQuery;
    const view = useMemo(() => parseFeatureUsageQuery(effectiveQuery), [effectiveQuery]);
    const { filters, search, order } = view;
    const canonicalQuery = featureUsageQueryString(view);

    useEffect(() => {
        if (arrivedFor.current === productId) return;
        arrivedFor.current = productId;
        if (!urlQuery && savedQuery) setSearchParams(savedQuery, { replace: true });
    }, [productId, urlQuery, savedQuery, setSearchParams]);

    useEffect(() => {
        saveFeatureView(productId, canonicalQuery);
    }, [productId, canonicalQuery]);

    // Filter and order changes add a history entry (Back undoes them); typing in search replaces it.
    const updateView = useCallback((patch, { replace = false } = {}) => {
        const next = featureUsageQueryString({
            ...view,
            ...patch,
            filters: { ...view.filters, ...(patch.filters || {}) },
        });
        if (next !== canonicalQuery) setSearchParams(next, { replace });
    }, [view, canonicalQuery, setSearchParams]);
    const setSearch = (value) => updateView({ search: value }, { replace: true });
    const setOrder = (value) => updateView({ order: value });
    const [panelOpen, setPanelOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const productReady = Boolean(selectedProduct && String(selectedProduct.id) === String(productId));

    const apiQuery = buildFeatureUsageParams(filters).toString();
    const waitingForDate = isCustomRangeIncomplete(filters);

    useEffect(() => {
        if (!productReady || waitingForDate) return undefined;

        let cancelled = false;
        setLoading(true);
        setError(null);

        fetchFeatureUsage(productId, new URLSearchParams(apiQuery))
            .then((result) => {
                if (!cancelled) setData(result);
            })
            .catch(() => {
                if (!cancelled) setError("Couldn't load settings usage. Check your connection and try again.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [productId, productReady, apiQuery, waitingForDate]);

    const features = useMemo(() => (data?.features || []).map((f) => ({ ...f, id: `${f.source}:${f.key}` })), [data]);
    const visible = useMemo(() => filterAndSortFeatures(features, search, order), [features, search, order]);
    const chartFeatures = visible.slice(0, CHART_LIMIT);
    const chips = activeFilterChips(filters, localeLabel);

    const changeFilter = useCallback((key, value) => {
        updateView({ filters: { [key]: value } });
    }, [updateView]);
    const resetFilter = (key) => {
        if (key === 'window') {
            updateView({ filters: { window: DEFAULT_FEATURE_FILTERS.window, since: '' } });
            return;
        }
        changeFilter(key, DEFAULT_FEATURE_FILTERS[key]);
    };
    const resetAll = () => updateView({ filters: DEFAULT_FEATURE_FILTERS });
    const closePanel = useCallback(() => setPanelOpen(false), []);

    const basis = data
        ? `Based on ${formatNumber(data.total_sites)} ${filters.only_production ? 'live ' : ''}`
            + `${filters.plan === 'pro' ? 'Pro ' : filters.plan === 'free' ? 'free ' : ''}sites`
            + (filters.window === 'custom' && filters.since
                ? ` that reported since ${formatIsoDate(filters.since)}`
                : ` that reported in the last ${Number(filters.window) || DEFAULT_FEATURE_FILTERS.window} days`)
            + (data.pro_sites && filters.plan === 'all' ? ` (${formatNumber(data.pro_sites)} with Pro)` : '')
            + (data.sample
                ? `. Settings measured on a fixed sample of ${formatNumber(data.sample.sites)} of these sites;`
                    + ' percentages are accurate to about ±1.5 points and counts marked ≈ are estimates.'
                : '')
        : '';

    const isEmpty = !loading && !error && data && features.length === 0;

    return (
        <div className="telemetry-page-wrapper">
            <header className="telemetry-header">
                <div className="telemetry-logo-section">
                    <Link to={`/stores/${storeId}/plugins/${productId}/dashboard`}><img src={smlogo} alt="Telemetry" /></Link>
                </div>
                <div className="telemetry-top">
                    <h1>Top Features</h1>
                    <div className="telemetry-top-right">
                        <DarkModeSwitch />
                        <AddProducts />
                        <Profile image={profileImage} />
                    </div>
                </div>
            </header>
            <main className="telemetry-main">
                <aside className="telemetry-sidebar">
                    <ToggleDropdown tabactive={1} tabactive2={1} />
                </aside>
                <section className="telemetry-content tf-page">
                    <div className="tf-filter-bar">
                        <div className="tf-filter-row">
                            <input
                                type="search"
                                className="tf-search"
                                placeholder="Search settings"
                                aria-label="Search settings"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <button
                                type="button"
                                className={`tf-filters-button${panelOpen ? ' is-open' : ''}`}
                                aria-expanded={panelOpen}
                                aria-controls="tf-filter-panel"
                                onClick={() => setPanelOpen((open) => !open)}
                            >
                                Filters
                                {chips.length > 0 && <span className="tf-count">{chips.length}</span>}
                            </button>
                            <div className="tf-download">
                                <DownloadDropdown
                                    allLabel="All settings"
                                    tableLabel="Table settings"
                                    tableData={featureCsvRows(visible)}
                                    columns={FEATURE_CSV_COLUMNS}
                                    filenameBase="top-features"
                                    onDownloadAll={async () => featureCsvRows(filterAndSortFeatures(features, '', 'most'))}
                                />
                            </div>
                        </div>

                        <FeatureUsageFilters
                            open={panelOpen}
                            filters={filters}
                            facets={data?.facets}
                            localeLabel={localeLabel}
                            onChange={changeFilter}
                            onReset={resetAll}
                            onClose={closePanel}
                        />

                        {chips.length > 0 && (
                            <div className="tf-chips">
                                {chips.map((chip) => (
                                    <span className="tf-chip" key={chip.key}>
                                        {chip.label}
                                        <button type="button" aria-label={`Remove ${chip.label}`} onClick={() => resetFilter(chip.key)}>×</button>
                                    </span>
                                ))}
                                <button type="button" className="tf-link-button" onClick={resetAll}>Clear all</button>
                            </div>
                        )}
                    </div>

                    {basis && !error && <p className="tf-basis">{basis}</p>}

                    {error && (
                        <div className="tf-state tf-state-error" role="alert">
                            <strong>Couldn&apos;t load settings usage</strong>
                            <span>{error}</span>
                        </div>
                    )}

                    {isEmpty && (
                        <div className="tf-state">
                            <strong>No settings data yet</strong>
                            <span>Usage appears after sites with diagnostics enabled send their next daily report. Try a wider filter.</span>
                        </div>
                    )}

                    {!error && !isEmpty && (
                        <>
                            <div className="telemetry-block-section tf-block">
                                <div className="tf-block-header">
                                    <div>
                                        <h2>{order === 'least' ? 'Least used settings' : 'Most used settings'}</h2>
                                        <p className="tf-muted">
                                            {`${order === 'least' ? 'Bottom' : 'Top'} ${CHART_LIMIT} by share of sites that have the setting turned on or filled in.`}
                                        </p>
                                    </div>
                                    <div className="tf-segmented" role="group" aria-label="Order">
                                        <button type="button" aria-pressed={order === 'most'} className={order === 'most' ? 'is-active' : ''} onClick={() => setOrder('most')}>Most used</button>
                                        <button type="button" aria-pressed={order === 'least'} className={order === 'least' ? 'is-active' : ''} onClick={() => setOrder('least')}>Least used</button>
                                    </div>
                                </div>
                                <div className="tf-legend">
                                    <span><i style={{ background: FEATURE_COLORS.core }} />Core setting</span>
                                    {chartFeatures.some((f) => f.source === 'pro') && (
                                        <span><i style={{ background: FEATURE_COLORS.pro }} />Pro setting (measured on Pro sites)</span>
                                    )}
                                    {chartFeatures.some((f) => f.default_on) && (
                                        <span><i className="tf-legend-hatch" />On by default</span>
                                    )}
                                </div>
                                {loading ? (
                                    <div className="spinner-container tf-chart-loading"><div className="spinner" /></div>
                                ) : chartFeatures.length > 0 ? (
                                    <FeatureUsageBarChart features={chartFeatures} />
                                ) : (
                                    <div className="tf-empty">No settings match your search.</div>
                                )}
                            </div>

                            <div className="telemetry-block-section tf-block">
                                <div className="tf-block-header">
                                    <div>
                                        <h2>All settings</h2>
                                        <p className="tf-muted">{`${visible.length} settings · click a row to see the most common values.`}</p>
                                    </div>
                                </div>
                                <div className="telemetry-data-table-container">
                                    <FeatureUsageTable features={visible} loading={loading} />
                                </div>
                            </div>
                        </>
                    )}
                </section>
            </main>
        </div>
    );
}
